/**
 * @fileoverview The agent console organizes agents into top-level categories
 * (Canvas, Data, QA). Each category owns an ordered list of interaction-site
 * subtypes. Today every category maps to a single subtype, but the abstraction
 * is built so a category can grow additional subtypes (e.g. a second Data agent
 * type alongside Catalog) without reworking the digests.
 *
 * A digest is scoped to a category. It can be further narrowed to one of the
 * category's subtypes via the `interaction_site` URL query param.
 * @owner @Appboy/intelligence
 */

import type { InteractionSite } from "./types";
import { INTERACTION_SITES } from "./types";

/** Top-level agent console categories, mirrored by the left-nav sections. */
export const AGENT_CATEGORIES = {
  CANVAS: "canvas",
  DATA: "data",
  QA: "qa",
} as const;

export type AgentCategory = (typeof AGENT_CATEGORIES)[keyof typeof AGENT_CATEGORIES];

/**
 * The interaction-site subtypes each category owns, in display order. The first
 * entry is the category's canonical subtype. Adding a subtype here is the only
 * change required for a category to offer a real Agent type choice.
 */
export const categorySubtypes: Record<AgentCategory, InteractionSite[]> = {
  [AGENT_CATEGORIES.CANVAS]: [INTERACTION_SITES.CANVAS],
  [AGENT_CATEGORIES.DATA]: [INTERACTION_SITES.CATALOG],
  [AGENT_CATEGORIES.QA]: [INTERACTION_SITES.CAMPAIGN_SETUP, INTERACTION_SITES.CANVAS_QA],
};

/** Feature-flag inputs that gate which subtypes are currently usable. */
export interface SubtypeAvailability {
  /** Whether the `agent_console_campaign_qa` feature is enabled. */
  campaignQaEnabled: boolean;
  /** Whether the `agent_console_canvas_qa` feature is enabled. */
  canvasQaEnabled: boolean;
}

/**
 * Returns the subtypes a category should expose right now, filtering out
 * feature-flagged subtypes that are not yet enabled. The static
 * `categorySubtypes` map stays the source of truth for ownership lookups
 * (`categoryForInteractionSite`); this is what the UI (digest scope, Agent type
 * filter, Create dropdown) should render.
 */
export const visibleCategorySubtypes = (
  category: AgentCategory,
  availability: SubtypeAvailability
): InteractionSite[] => {
  return categorySubtypes[category].filter((subtype) => {
    if (subtype === INTERACTION_SITES.CAMPAIGN_SETUP) {
      return availability.campaignQaEnabled;
    }
    if (subtype === INTERACTION_SITES.CANVAS_QA) {
      return availability.canvasQaEnabled;
    }
    return true;
  });
};

const categoryByInteractionSite: Partial<Record<InteractionSite, AgentCategory>> = Object.entries(
  categorySubtypes
).reduce<Partial<Record<InteractionSite, AgentCategory>>>((acc, [category, subtypes]) => {
  subtypes.forEach((subtype) => {
    acc[subtype] = category as AgentCategory;
  });
  return acc;
}, {});

/** Returns the category that owns the given interaction site, if any. */
export const categoryForInteractionSite = (interactionSite: number | undefined): AgentCategory | undefined => {
  if (interactionSite === undefined) {
    return undefined;
  }
  return categoryByInteractionSite[interactionSite as InteractionSite];
};

/** Returns true when the interaction site is a valid subtype of the category. */
export const isSubtypeOfCategory = (interactionSite: number, category: AgentCategory): boolean => {
  return categorySubtypes[category].includes(interactionSite as InteractionSite);
};
